import { useMemo } from "react";
import LazyApexChart from '../../../client/components/LazyApexChart';
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { chartColors } from "../chart/chartColors";

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

export default function RollingWinRateWidget() {
  const { analyticsData, isDark, themeId, t } = useDashboardData();

  const chartData = useMemo(() => {
    if (!analyticsData || analyticsData.rollingWinRate.length === 0) return null;
    // Filter to the selected window if needed - use the data as-is (pre-calculated for window=20)
    // Since the context provides rollingWinRate, we use it directly
    return analyticsData.rollingWinRate;
  }, [analyticsData]);

  const chartOptions = useMemo(() => {
    if (!chartData) return null;
    const c = chartColors();
    return {
      chart: {
        type: "line" as const,
        toolbar: { show: false },
        background: "transparent",
        foreColor: c.axis,
        animations: { enabled: false },
      },
      stroke: {
        curve: "smooth" as const,
        width: 2.5,
        colors: [c.accent],
      },
      xaxis: {
        categories: chartData.map((d) => d.tradeIndex),
        tickAmount: 10,
        labels: {
          style: { colors: c.axis, fontSize: "10px" },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        min: 0,
        max: 100,
        labels: {
          style: { colors: c.axis, fontSize: "11px" },
          formatter: (v: number) => v.toFixed(0) + "%",
        },
      },
      grid: {
        borderColor: c.grid,
        strokeDashArray: 4,
      },
      annotations: {
        yaxis: [
          {
            y: 50,
            borderColor: c.axis,
            borderWidth: 1,
            strokeDashArray: 4,
            label: {
              text: "50%",
              style: {
                color: c.axis,
                background: "transparent",
                fontSize: "10px",
              },
            },
          },
        ],
      },
      tooltip: {
        theme: isDark ? "dark" : "light",
        y: { formatter: (v: number) => v.toFixed(1) + "%" },
      },
      markers: { size: 0 },
      dataLabels: { enabled: false },
      legend: { show: false },
    };
  }, [chartData, isDark]);

  if (!analyticsData || analyticsData.rollingWinRate.length === 0) {
    return (
      <Card className="h-full">
        <CardContent className="h-full p-4">
          <EmptyState label={t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardSectionHeader
        label={t.analytics.rollingWinRateTitle}
        isDark={isDark}
      />
      <CardContent className="px-5 pb-5 pt-1">
        {chartOptions && chartData ? (
          <LazyApexChart
            key={`rolling-win-rate-widget-${themeId}`}
            options={chartOptions}
            series={[
              {
                name: t.analytics.winRate,
                data: chartData.map(
                  (d) => Math.round(d.winRate * 10) / 10,
                ),
              },
            ]}
            type="line"
            height={240}
          />
        ) : (
          <EmptyState label={t.analytics.noDataAvailable} />
        )}
      </CardContent>
    </Card>
  );
}
